import * as segmentUtils from "utils/segments/segmentUtils";

import { SegmentSimple, SegmentVisibility } from "types";
import { useAllSegments, useSegmentFavorites } from "queries/segmentQueries";
import { useEffect, useMemo, useRef, useState } from "react";

type UseSegmentSelectSearchArgs = {
  initSelectedUuid: string | null | undefined;
  onInitialItemsLoaded: (data: { items: SegmentSimple[]; initialSelected: SegmentSimple }) => void;
  initSegmentVisibility?: SegmentVisibility;
  initIsSegmentFavorites?: boolean;
  customSegments?: SegmentSimple[];
};

type SelectChangeEvent = {
  target?: {
    value?: SegmentSimple[];
  };
};

/**
 * Hook designed to be used in conjunction with the CSSegmentFilterInputDropdown component.
 * Returns the props you need for both the `CSSegmentFilterInputDropdown` and `CSSelectSearch` components.
 *
 * The `onInitialItemsLoaded` callback is provided so you can update hook form via `formApi.setValue`.
 *
 * @example
 *
 * const segmentSelectSearch = useSegmentSelectSearch({
 *   initSelectedUuid: questionDefnItem.segmentUuid,
 *   onInitialItemsLoaded: ({ initialSelected }) => formApi.setValue("segment", [initialSelected]),
 * });
 *
 * ...
 *
 * <CSSegmentFilterInputDropdown
 *   {...segmentSelectSearch.filterInputProps}
 *   searchInput={
 *     <CSFormInput.SelectSearch
 *       {...segmentSelectSearch.selectSearchProps}
 *       name="segment"
 *       formApi={formApi}
 *       labelKey="name"
 *       valueKey="uuid"
 *       rules={{ ...segmentSelectSearch.rules }}
 *     />
 *   }
 * />
 */
export const useSegmentSelectSearch = ({
  initSelectedUuid = "",
  onInitialItemsLoaded,
  initIsSegmentFavorites = false,
  initSegmentVisibility = "",
  customSegments,
}: UseSegmentSelectSearchArgs) => {
  const isInitialized = useRef(false);
  const [segmentVisibility, setSegmentVisibility] = useState<SegmentVisibility>(initSegmentVisibility);
  const [isSegmentFavorites, setIsSegmentFavorites] = useState<boolean>(initIsSegmentFavorites);
  const [toggleText, setToggleText] = useState<string | null>(null);

  const allSegments = useAllSegments();
  const segmentsFiltered = useAllSegments({
    select: segmentUtils.selectSegmentsByVisibility(segmentVisibility, customSegments),
  });
  const segmentFavoritesData = useSegmentFavorites({
    select: segmentUtils.selectSegmentsByVisibility(segmentVisibility, customSegments),
  });

  // Handle any initializations when the full list of segments is loaded
  // This allows consumers to react - for example, setting the value into react hook form.
  useEffect(() => {
    if (allSegments.data && initSelectedUuid && !isInitialized.current) {
      isInitialized.current = true;
      const initialSelected = allSegments.data.find((s) => s.uuid === initSelectedUuid);
      initialSelected && onInitialItemsLoaded({ items: allSegments.data, initialSelected });
      setToggleText((current) => (current === null ? (initialSelected?.name ?? "") : current));
    }
  }, [allSegments, initSelectedUuid, onInitialItemsLoaded]);

  const result = useMemo(() => {
    return {
      allSegments: allSegments,
      setToggleText,
      filterInputProps: {
        toggleText: toggleText ?? "",
        onChangeFavorites: setIsSegmentFavorites,
        onChangeVisibility: setSegmentVisibility,
        segmentVisibility,
        segmentFavorites: isSegmentFavorites,
        labelText: "Segment",
        showTooltip: false,
      },
      selectSearchProps: {
        placeholder: "Select segment",
        options: isSegmentFavorites ? segmentFavoritesData : segmentsFiltered,
        initialValue: initSelectedUuid ? [initSelectedUuid] : [],
      },
      rules: {
        /**
         * By default, set the toggle text to a comma list of segment names.
         */
        onChange: (evt: SelectChangeEvent) => {
          setToggleText(evt.target?.value?.map((s) => s.name).join(", ") ?? "");
        },
      },
    };
  }, [
    toggleText,
    allSegments,
    segmentsFiltered,
    segmentFavoritesData,
    segmentVisibility,
    isSegmentFavorites,
    initSelectedUuid,
  ]);

  return result;
};
